import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import ResultsFiltersButtons from '@Containers/ResultsFilters/ResultsFiltersComponents/ResultsFiltersButtons';
import ListOfFilters from '@Containers/ResultsFilters/ResultsFiltersComponents/ListOfFilters';
import SummaryContainer from '@Containers/Summary/SummaryContainer';
import Drawer from '@Components/Drawer/Drawer';
import { DRAWER_VIEW_ASSISTANT, DRAWER_VIEW_FILTERS } from '@Components/Drawer/constants';
import useBreakpoint from '@Hooks/useBreakpoint';
import { SUMMARY_SCOPE, SUMMARY_SELECTION_ERROR, isSummarySelectionValid } from '@Containers/Summary/constants';
import { ASSISTANT_FEATURES, trackAssistantFeatureClick } from '@Containers/Summary/analytics';
import { resolveSummaryRequestParams } from '@Containers/Summary/summaryContext';
import FilterAppliedNudge from './FilterAppliedNudge';
import useDrawerAvailableHeight from './hooks/useDrawerAvailableHeight';
import useFilterActionFocus from './hooks/useFilterActionFocus';

import './ResultsFilters.scss';

// AR-7.3: one Drawer instance now serves both the Assistant and Filters views, replacing the two
// previously-separate mount points (this component's own Drawer, and `SummaryContainer` mounted
// inline in `ResultsPage.jsx`). `activeView` is left uncontrolled — Drawer already defaults to
// Assistant when eligible, Filters otherwise, and switching views is a rail-only concern this
// component owns so it can switch from Filters to Assistant when a newly-loaded breadcrumb
// becomes eligible without waiting for the drawer to remount.
export default function ResultsFilters({
  className,
  open,
  setOpenFilters,
  openHeatmapIndicator,
  heatmapFullScreenModeIndicator,
  assistantEligible,
  isFocusMode,
  activeBreadcrumb,
  params,
  selectedItems,
  chainKey,
  appliedFilterSummary,
  hasSelectedFilters,
  resultsLoading,
  generateSummary,
}) {
  const state = open ? 'open' : 'close';
  const [activeView, setActiveView] = useState(assistantEligible ? DRAWER_VIEW_ASSISTANT : DRAWER_VIEW_FILTERS);
  const [selectionError, setSelectionError] = useState(null);
  const [acknowledgedFilterResultName, setAcknowledgedFilterResultName] = useState(null);
  const drawerRef = useRef(null);
  const previousAssistantEligible = useRef(assistantEligible);
  const isTabletUp = useBreakpoint('tablet');
  const isDesktopUp = useBreakpoint('desktop');
  const renderPanelAsModal = !isTabletUp;
  const trackingContext = activeBreadcrumb?.context ?? params?.context;
  const currentAppliedFilter =
    appliedFilterSummary?.resultName === (params?.resultName ?? activeBreadcrumb?.resultName) ? appliedFilterSummary : null;
  const showFilterAppliedNudge =
    assistantEligible && currentAppliedFilter && !hasSelectedFilters && acknowledgedFilterResultName !== currentAppliedFilter.resultName;
  const { drawerHeadingRef, filterNudgePrimaryActionRef, handleApplyComplete, handleClearComplete } = useFilterActionFocus({
    activeResultName: params?.resultName ?? activeBreadcrumb?.resultName,
    hasSelectedFilters,
    renderPanelAsModal,
    resultsLoading,
    showFilterAppliedNudge,
  });

  useDrawerAvailableHeight(drawerRef, isDesktopUp);

  useEffect(() => {
    if (!assistantEligible) {
      setActiveView(DRAWER_VIEW_FILTERS);
    } else if (!previousAssistantEligible.current) {
      setActiveView(DRAWER_VIEW_ASSISTANT);
    }
    previousAssistantEligible.current = assistantEligible;
  }, [assistantEligible]);

  useEffect(() => {
    if (isSummarySelectionValid(selectedItems)) {
      setSelectionError(null);
    }
  }, [selectedItems]);

  const generateFromFilterNudge = (scope) => {
    trackAssistantFeatureClick(
      scope === SUMMARY_SCOPE.SELECTED ? ASSISTANT_FEATURES.SUMMARIZE_SELECTED : ASSISTANT_FEATURES.SUMMARIZE_RESULTS,
      trackingContext
    );

    if (scope === SUMMARY_SCOPE.SELECTED && !isSummarySelectionValid(selectedItems)) {
      setSelectionError(SUMMARY_SELECTION_ERROR);
      return;
    }

    setSelectionError(null);
    generateSummary({
      ...resolveSummaryRequestParams({ activeBreadcrumb, params, chainKey }),
      scope,
      selectedItems: scope === SUMMARY_SCOPE.SELECTED ? selectedItems : [],
      trackingBreadcrumb: activeBreadcrumb,
    });
    setAcknowledgedFilterResultName(currentAppliedFilter?.resultName || null);
    setActiveView(DRAWER_VIEW_ASSISTANT);
  };

  const dismissFilterAppliedNudge = useCallback(() => {
    drawerHeadingRef.current?.focus();
    setAcknowledgedFilterResultName(currentAppliedFilter?.resultName || null);
  }, [currentAppliedFilter?.resultName, drawerHeadingRef]);

  let filtersBottomPanel = null;
  if (showFilterAppliedNudge) {
    filtersBottomPanel = (
      <FilterAppliedNudge
        key={currentAppliedFilter.resultName}
        className="drawer-panel__bottom-surface--enter"
        appliedFilter={currentAppliedFilter}
        onDismiss={dismissFilterAppliedNudge}
        onSummarizeTop={() => generateFromFilterNudge(SUMMARY_SCOPE.TOP)}
        onSummarizeSelected={() => generateFromFilterNudge(SUMMARY_SCOPE.SELECTED)}
        primaryActionRef={filterNudgePrimaryActionRef}
        selectionError={selectionError}
      />
    );
  } else if (hasSelectedFilters) {
    filtersBottomPanel = (
      <ResultsFiltersButtons
        className="drawer-panel__bottom-surface--enter"
        onApplyComplete={handleApplyComplete}
        onClearComplete={handleClearComplete}
      />
    );
  }

  const filtersContent = (
    <>
      {/*
        RX-23573: not a `region` landmark. This sits directly inside the Drawer's "Filters"
        complementary landmark, so a nested region here just double-announced the same area
        (redundant landmark). Plain <div> keeps the styling without the duplicate landmark.
      */}
      <div
        className={classnames(
          'results-filters',
          'u-els-padding-bottom',
          `results-filters-${state}`,
          {
            [heatmapFullScreenModeIndicator ? 'results-filters-heatmap-view-full-screen' : 'results-filters-heatmap-view']:
              openHeatmapIndicator,
          },
          className
        )}>
        <ListOfFilters />
      </div>
      {filtersBottomPanel}
    </>
  );

  const closeModalPanelBeforeReferenceNavigation = useCallback(() => setOpenFilters(false), [setOpenFilters]);
  const trackDrawerCollapse = useCallback(
    () => trackAssistantFeatureClick(ASSISTANT_FEATURES.COLLAPSE_SIDE_PANE, trackingContext),
    [trackingContext]
  );
  const trackDrawerViewClick = useCallback(
    (view) =>
      trackAssistantFeatureClick(
        view === DRAWER_VIEW_ASSISTANT ? ASSISTANT_FEATURES.ASSISTANT_SIDE_PANE : ASSISTANT_FEATURES.FILTERS_SIDE_PANE,
        trackingContext
      ),
    [trackingContext]
  );
  const assistantContent = (
    <SummaryContainer
      activeBreadcrumb={activeBreadcrumb}
      params={params}
      selectedItems={selectedItems}
      onBeforeReferenceNavigation={renderPanelAsModal ? closeModalPanelBeforeReferenceNavigation : undefined}
    />
  );

  return (
    <Drawer
      isOpen={open}
      setIsOpen={setOpenFilters}
      assistantEligible={assistantEligible}
      activeView={activeView}
      onViewChange={setActiveView}
      onViewClick={trackDrawerViewClick}
      onCollapse={trackDrawerCollapse}
      hasFilterApplied={!!currentAppliedFilter}
      isFocusMode={isFocusMode}
      assistantContent={assistantContent}
      filtersContent={filtersContent}
      headingRef={drawerHeadingRef}
      rootRef={drawerRef}
      renderPanelAsModal={renderPanelAsModal}
    />
  );
}

ResultsFilters.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpenFilters: PropTypes.func.isRequired,
  openHeatmapIndicator: PropTypes.bool.isRequired,
  heatmapFullScreenModeIndicator: PropTypes.bool,
  assistantEligible: PropTypes.bool,
  isFocusMode: PropTypes.bool,
  activeBreadcrumb: PropTypes.object,
  params: PropTypes.object,
  selectedItems: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.string.isRequired,
      id: PropTypes.string,
    })
  ),
  chainKey: PropTypes.string,
  appliedFilterSummary: PropTypes.object,
  hasSelectedFilters: PropTypes.bool,
  resultsLoading: PropTypes.bool,
  generateSummary: PropTypes.func.isRequired,
};
